
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileMenu from "./navbar/MobileMenu";
import DesktopNav from "./navbar/DesktopNav";
import { NavItem } from "@/types/navbar";

const Navbar = () => {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(80);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Close menu on page change
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Scroll handler for header styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  
  // Update header height and CSS vars to avoid content overlap
  useEffect(() => {
    const el = headerRef.current;

    const applyHeight = () => {
      const h = el?.offsetHeight ?? 80;
      setHeaderHeight(h);
      document.documentElement.style.setProperty('--header-height-mobile', `${h}px`);
      document.documentElement.style.setProperty('--header-height-desktop', `${h}px`);
    };

    // Initial
    applyHeight();

    // Observe dynamic size changes (fonts wrap, menu open, etc.)
    let ro: ResizeObserver | undefined;
    if (el && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => {
        applyHeight();
      });
      ro.observe(el);
    }

    // Window resize & orientation changes
    window.addEventListener('resize', applyHeight);

    return () => {
      window.removeEventListener('resize', applyHeight);
      ro?.disconnect();
    };
  }, [isMenuOpen, scrolled, location.pathname]);

  // Smooth scroll to anchors
  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    if (hash.startsWith('#')) {
      e.preventDefault();
      const id = hash.substring(1);
      const element = document.getElementById(id);
      
      if (element) {
        const header = document.querySelector('.site-header') as HTMLElement | null;
        const headerOffset = header?.offsetHeight ?? 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
        
        window.history.pushState(null, '', hash);
        
        element.setAttribute('tabindex', '-1');
        element.focus({ preventScroll: true });
      }
    }
  }, []);

  // Main navigation items (without "Rechtliches")
  const navItems: NavItem[] = [
    { name: "Start", link: "/" },
    { name: "Coaching", link: "/coaching" },
    { name: "Consulting", link: "/consulting" },
    { name: "Outdoor-Coaching", link: "/outdoor-coaching" },
    { name: "Preise", link: "/preise" },
    { name: "Blog", link: "/blog" },
    { name: "Über mich", link: "/ueber-mich" },
    { name: "Kontakt", link: "/kontakt" },
    { name: "FAQ", link: "/faq" },
  ];


  const bookingUrl = "https://zeeg.me/joerghoermann";

  return (
    <>
      <header
        ref={headerRef}
        className={cn(
          "site-header fixed top-0 w-full z-50 transition-all duration-300",
          scrolled || isMenuOpen 
            ? "bg-white/95 shadow-sm py-2" 
            : "bg-transparent py-3 md:py-4"
        )}
        style={{ 
          backdropFilter: scrolled ? 'blur(8px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(8px)' : 'none' 
        }}
      >
        <div className="container px-4 sm:px-6 flex items-center justify-between">
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              className="flex flex-col items-start focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded transition-colors"
              aria-label="Zur Startseite"
              onClick={closeMenu}
            >
              <div className="flex items-center whitespace-nowrap">
                <img 
                  src="/images/jh-favicon.png" 
                  alt="Jörg Hörmann Logo"
                  className="w-7 h-7 mr-2"
                />
                <span className="text-lg sm:text-xl font-bold text-joerg-800 flex items-center">
                  Jörg Hörmann
                  <ChevronRight className="h-4 w-4 text-primary ml-1 hidden sm:inline-block" aria-hidden="true" />
                </span>
              </div>
              <p className="hidden xl:block text-xs mt-0.5 text-joerg-700 font-medium">
                Executive & Finanzcoach
              </p>
            </Link>
          </div>

          {isMobile ? (
            <button
              onClick={toggleMenu}
              className="p-2 text-joerg-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded z-[101]"
              aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? (
                <X size={24} aria-hidden="true" />
              ) : (
                <Menu size={24} aria-hidden="true" />
              )}
            </button>
          ) : (
            <DesktopNav 
              navItems={navItems}
              bookingUrl={bookingUrl}
              scrolled={scrolled}
              handleAnchorClick={handleAnchorClick}
            />
          )}
        </div>
      </header>
      
      {/* Mobile menu rendered outside the header for proper layering */}
      {isMobile && (
        <MobileMenu 
          isOpen={isMenuOpen}
          navItems={navItems}
          bookingUrl={bookingUrl}
          handleAnchorClick={handleAnchorClick}
          onClose={closeMenu}
        />
      )}
    </>
  );
};

export default Navbar;
